import React from 'react';
import { Link } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import '../styles/BlogsPage.css';

export default function AboutPage() {
  return (
    <div className="blogs-page">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg blogs-navbar">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
          </Link>
          <div className="d-flex align-items-center gap-3">
            <Link to="/blogs" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-newspaper me-2"></i>Blog
            </Link>
            <Link to="/" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-arrow-left me-2"></i>Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="blog-detail-hero mb-0 pb-0">
        <div className="container">
          <h1 className="blog-detail-title">About MeetingPoint</h1>
          <p className="blog-detail-excerpt">Meet the humans who think your data belongs to you.</p>
        </div>
      </header>

      {/* Content */}
      <article className="container blog-detail-content">
        <div className="blog-detail-body privacy-body">

          <div className="text-center mt-2">
            <h2 className="header-text">Finally, a <span className="gradient-text">MeetingPoint</span></h2>
          </div>
          <div className="text-center mb-5 pt-2">
            <h2 className="header-text">That Doesn't Want Your <span className="gradient-text">Life Story.</span></h2>
          </div>

          <h3>Our Mission: Privacy Without the Paperwork</h3>
          <p>
            The internet was originally designed to be a decentralized network of peers. Over the last decade, however, it has become a series of "walled gardens" where every simple interaction requires a login, a password, and a permanent record in a corporate database.
          </p>
          <p>
            MeetingPoint was created to bring back the original spirit of the web. We believe that you shouldn't have to trade your identity just to have a conversation.
          </p>

          <h3>Why We Built This</h3>
          <p>
            We noticed a gap in the digital world. You have big corporate tools for "official" meetings and encrypted apps for "private" messaging, but both require you to create an account, download software, or sync your contacts.
          </p>
          <p>
            We wanted to build a "MeetingPoint" — a digital neutral zone. No accounts, no tracking, and no footprint. Just a URL and a direct connection.
          </p>

          <h3>How We Are Different</h3>
          <p>
            Most platforms are built on a Client-Server model, where your data has to pass through their "house" before it gets to your friend. MeetingPoint is built on Peer-to-Peer (P2P) technology.
          </p>
          <ul>
            <li><strong>Zero Knowledge:</strong> We don't know who you are, and we don't want to.</li>
            <li><strong>No Storage:</strong> Since your data moves directly between browsers, there is no central server to hack and no database to leak.</li>
            <li><strong>Browser-Native:</strong> No plugins, no "exe" files, and no barriers.</li>
          </ul>

          <h3>Features</h3>
          <ul>
            <li><strong>Video & Audio Calls</strong> — Group calls with up to 4 participants</li>
            <li><strong>Text Messaging</strong> — Real-time chat with emoji support</li>
            <li><strong>File Sharing</strong> — Transfer files directly between devices up to 100MB</li>
            <li><strong>Screen Sharing</strong> — Share your screen for presentations and collaboration</li>
            <li><strong>Link Previews</strong> — Automatic rich previews for shared URLs</li>
            <li><strong>Low Bandwidth Mode</strong> — Dynamic codec negotiation for challenging networks</li>
          </ul>

          <h3>Who Uses MeetingPoint?</h3>
          <p>From freelancers to gamers, from journalists to therapists — MeetingPoint serves anyone who values private, instant communication:</p>
          <ul>
            <li><strong>Freelancers</strong> — Conduct video reviews without exchanging personal details</li>
            <li><strong>Remote Teams</strong> — Quick sync calls without enterprise software overhead</li>
            <li><strong>Gamers</strong> — Instant voice coordination with zero setup</li>
            <li><strong>Healthcare</strong> — Confidential consultations with no records</li>
            <li><strong>Journalists</strong> — Source protection with no metadata trail</li>
            <li><strong>Marketplace Sellers</strong> — Live product verification without swapping phone numbers</li>
          </ul>

          <h3>Our Commitment to Transparency</h3>
          <p>
            While we use tools like Google AdSense to keep the lights on and keep this service free for everyone, we remain fiercely protective of our core architecture. Ads are served on our informational pages, but your actual "MeetingPoint" remains a private, encrypted tunnel between you and your peer.
          </p>

          <h3>The Team</h3>
          <p>
            MeetingPoint is a project by developers and privacy enthusiasts who are tired of "Data-as-a-Product" business models. We are constantly working to refine the WebRTC protocols that make accountless communication possible.
          </p>

          <h3>Get In Touch</h3>
          <p>
            Have questions or feedback? We'd love to hear from you. Reach out at{' '}
            <a href="mailto:admin@meetingpoint.chat" className="text-decoration-underline">admin@meetingpoint.chat</a>.
          </p>
        </div>
      </article>

      {/* Footer */}
      <footer className="blogs-footer">
        <div className="container text-center">
          <p className="mb-1">&copy; {new Date().getFullYear()} MeetingPoint. All rights reserved.</p>
          <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
            <Link to="/" className="text-muted">Home</Link>
            <Link to="/blogs" className="text-muted">Blog</Link>
            <Link to="/faq" className="text-muted">FAQ</Link>
            <Link to="/contact" className="text-muted">Contact</Link>
            <Link to="/privacy" className="text-muted">Privacy Policy</Link>
            <Link to="/terms" className="text-muted">Terms</Link>
            <Link to="/disclaimer" className="text-muted">Disclaimer</Link>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}
